/**
 * ShareCardBase — offscreen React component rendered at native pixel size.
 * Each template wraps this with its own layout. Uses inline styles only
 * (no Tailwind) because html-to-image captures computed styles.
 */

import React from 'react';
import type { ShareTarget, ShareFormat, SHARE_FORMAT_SPECS } from '../ShareTarget';

export interface ShareCardProps {
  target: ShareTarget;
  format: ShareFormat;
  spec: (typeof SHARE_FORMAT_SPECS)[ShareFormat];
}

/* Farmly brand colors (HSL values from index.css) */
export const FARMLY_COLORS = {
  deepForest: 'hsl(135, 11%, 21%)',
  warmSand: 'hsl(36, 33%, 93%)',
  accent: 'hsl(8, 50%, 37%)',
  muted: 'hsl(28, 9%, 40%)',
  white: '#FFFFFF',
} as const;

export const FARMLY_FONTS = {
  display: "'DM Serif Display', Georgia, serif",
  body: "'Inter', system-ui, sans-serif",
} as const;

/** Gradient overlay for readability on hero images */
export function GradientOverlay({ direction = 'to top', intensity = 'normal' }: { direction?: string; intensity?: 'normal' | 'soft' }) {
  const opacity = intensity === 'soft' ? 0.55 : 0.7;
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: `linear-gradient(${direction}, rgba(0,0,0,${opacity}) 0%, rgba(0,0,0,${opacity * 0.28}) 40%, transparent 70%)`,
        zIndex: 1,
      }}
    />
  );
}

/** Farmly badge text in bottom corner */
export function FarmlyBadge({ style }: { style?: React.CSSProperties }) {
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 40,
        right: 40,
        zIndex: 3,
        fontFamily: FARMLY_FONTS.display,
        fontSize: 28,
        color: FARMLY_COLORS.warmSand,
        letterSpacing: '0.02em',
        opacity: 0.9,
        ...style,
      }}
    >
      farmly.se
    </div>
  );
}

/** Fallback gradient background when no hero image */
export function FallbackBg({ title, w, h, variant }: { title: string; w: number; h: number; variant?: 'default' | 'place' }) {
  const initial = title.charAt(0).toUpperCase();
  const isPlace = variant === 'place';
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: isPlace
          ? `linear-gradient(145deg, ${FARMLY_COLORS.deepForest} 0%, hsl(135, 18%, 28%) 50%, hsl(28, 15%, 30%) 100%)`
          : `linear-gradient(135deg, ${FARMLY_COLORS.deepForest} 0%, hsl(135, 15%, 30%) 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <span
        style={{
          fontFamily: FARMLY_FONTS.display,
          fontSize: Math.min(w, h) * (isPlace ? 0.22 : 0.4),
          color: isPlace ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.08)',
          userSelect: 'none',
        }}
      >
        {initial}
      </span>
    </div>
  );
}

/** Hero image with crossorigin for CORS-safe capture */
export function HeroImage({ src, w, h }: { src: string; w: number; h: number }) {
  return (
    <img
      src={src}
      crossOrigin="anonymous"
      referrerPolicy="no-referrer"
      style={{
        position: 'absolute',
        inset: 0,
        width: w,
        height: h,
        objectFit: 'cover',
      }}
      alt=""
    />
  );
}
